import React, { useState, useEffect, useCallback } from 'react';
import { faqAdminService } from '../../services/publicMarketService';
import { useAppSelector } from '../../store/hooks';

interface FaqItem {
  id: number;
  question: string;
  answer: string;
  displayOrder: number;
  visible: boolean;
}

interface FaqAdminData {
  id: number;
  published: boolean;
  maskAll: boolean;
  pageTitle: string;
  pageSubtitle: string;
  updatedBy?: string;
  updatedAt?: string;
  items: FaqItem[];
}

// ── tiny helpers ──────────────────────────────────────────────────────────────

const Card: React.FC<{ children: React.ReactNode; style?: React.CSSProperties }> = ({ children, style }) => (
  <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, padding: '20px 24px', marginBottom: 20, ...style }}>
    {children}
  </div>
);

const Toggle: React.FC<{ checked: boolean; onChange: () => void; disabled?: boolean }> = ({ checked, onChange, disabled }) => (
  <div
    onClick={() => !disabled && onChange()}
    style={{
      width: 40, height: 22, borderRadius: 11, position: 'relative',
      background: checked ? '#2563eb' : '#cbd5e1',
      cursor: disabled ? 'not-allowed' : 'pointer',
      transition: 'background 0.2s', flexShrink: 0, opacity: disabled ? 0.5 : 1,
    }}
  >
    <div style={{
      position: 'absolute', top: 3, left: checked ? 21 : 3,
      width: 16, height: 16, borderRadius: '50%', background: '#fff',
      transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
    }} />
  </div>
);

const Textarea: React.FC<{ label: string; value: string; onChange: (v: string) => void; placeholder?: string; rows?: number }> = ({ label, value, onChange, placeholder, rows = 4 }) => (
  <div style={{ marginBottom: 12 }}>
    <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 4 }}>{label}</label>
    <textarea
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      style={{
        width: '100%', padding: '8px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8,
        fontSize: 13, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box',
        outline: 'none', lineHeight: 1.5,
      }}
    />
  </div>
);

const Input: React.FC<{ label: string; value: string; onChange: (v: string) => void; placeholder?: string }> = ({ label, value, onChange, placeholder }) => (
  <div style={{ marginBottom: 12 }}>
    <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 4 }}>{label}</label>
    <input
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      style={{
        width: '100%', padding: '8px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8,
        fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none',
      }}
    />
  </div>
);

// ── main component ────────────────────────────────────────────────────────────

const AdminFaqManager: React.FC = () => {
  const adminUser = useAppSelector((s: any) => s.auth.adminUser);
  const isSuperAdmin = adminUser?.role === 'SUPER_ADMIN';
  const canManage = isSuperAdmin || !!(adminUser?.permissions?.includes('MANAGE_FAQ'));
  const canView   = canManage || !!(adminUser?.permissions?.includes('VIEW_FAQ'));

  const [data, setData] = useState<FaqAdminData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

  // Page config edit
  const [configEditing, setConfigEditing] = useState(false);
  const [configForm, setConfigForm] = useState({ pageTitle: '', pageSubtitle: '' });

  // Item forms
  const [addingItem, setAddingItem] = useState(false);
  const [newItemForm, setNewItemForm] = useState({ question: '', answer: '' });
  const [editingItemId, setEditingItemId] = useState<number | null>(null);
  const [editItemForm, setEditItemForm] = useState({ question: '', answer: '' });

  const showToast = useCallback((msg: string, type: 'success' | 'error' = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await faqAdminService.getConfig();
      setData(res);
      setConfigForm({ pageTitle: res.pageTitle ?? '', pageSubtitle: res.pageSubtitle ?? '' });
    } catch { showToast('Failed to load FAQ config', 'error'); }
    finally { setLoading(false); }
  }, [showToast]);

  useEffect(() => { load(); }, [load]);

  // ── publish / unpublish ──────────────────────────────────────────────────

  const handlePublish = async () => {
    if (!canManage) return;
    setPublishing(true);
    try {
      const res = await faqAdminService.publish();
      setData(res);
      showToast('FAQ page published — now live at /faq');
    } catch { showToast('Failed to publish', 'error'); }
    finally { setPublishing(false); }
  };

  const handleUnpublish = async () => {
    if (!canManage) return;
    setPublishing(true);
    try {
      const res = await faqAdminService.unpublish();
      setData(res);
      showToast('FAQ page unpublished');
    } catch { showToast('Failed to unpublish', 'error'); }
    finally { setPublishing(false); }
  };

  // ── mask all ────────────────────────────────────────────────────────────

  const handleToggleMask = async () => {
    if (!data || !canManage) return;
    const newVal = !data.maskAll;
    setData(prev => prev ? { ...prev, maskAll: newVal } : prev);
    try {
      const res = await faqAdminService.updateConfig({ maskAll: newVal });
      setData(res);
      showToast(newVal ? 'All FAQ content masked' : 'FAQ content unmasked');
    } catch {
      setData(prev => prev ? { ...prev, maskAll: !newVal } : prev);
      showToast('Failed to save change', 'error');
    }
  };

  // ── config edit ─────────────────────────────────────────────────────────

  const handleConfigSave = async () => {
    setSaving(true);
    try {
      const res = await faqAdminService.updateConfig(configForm);
      setData(res);
      setConfigEditing(false);
      showToast('Page settings saved');
    } catch { showToast('Failed to save', 'error'); }
    finally { setSaving(false); }
  };

  // ── add item ────────────────────────────────────────────────────────────

  const handleAddItem = async () => {
    if (!newItemForm.question.trim() || !newItemForm.answer.trim()) {
      showToast('Question and answer are required', 'error');
      return;
    }
    setSaving(true);
    try {
      await faqAdminService.createItem(newItemForm.question.trim(), newItemForm.answer.trim());
      const res = await faqAdminService.getConfig();
      setData(res);
      setNewItemForm({ question: '', answer: '' });
      setAddingItem(false);
      showToast('FAQ item added');
    } catch { showToast('Failed to add item', 'error'); }
    finally { setSaving(false); }
  };

  // ── edit item ───────────────────────────────────────────────────────────

  const startEdit = (item: FaqItem) => {
    setEditingItemId(item.id);
    setEditItemForm({ question: item.question, answer: item.answer });
  };

  const handleEditSave = async (id: number) => {
    setSaving(true);
    try {
      await faqAdminService.updateItem(id, editItemForm);
      const res = await faqAdminService.getConfig();
      setData(res);
      setEditingItemId(null);
      showToast('FAQ item updated');
    } catch { showToast('Failed to update', 'error'); }
    finally { setSaving(false); }
  };

  // ── toggle item visibility ──────────────────────────────────────────────

  const handleToggleItemVisible = async (item: FaqItem) => {
    const newVal = !item.visible;
    setData(prev => prev ? { ...prev, items: prev.items.map(it => it.id === item.id ? { ...it, visible: newVal } : it) } : prev);
    try {
      await faqAdminService.updateItem(item.id, { visible: newVal });
      showToast(`Item ${newVal ? 'shown' : 'hidden'}`);
    } catch {
      setData(prev => prev ? { ...prev, items: prev.items.map(it => it.id === item.id ? { ...it, visible: !newVal } : it) } : prev);
      showToast('Failed to save', 'error');
    }
  };

  // ── delete item ─────────────────────────────────────────────────────────

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this FAQ item?')) return;
    try {
      await faqAdminService.deleteItem(id);
      setData(prev => prev ? { ...prev, items: prev.items.filter(it => it.id !== id) } : prev);
      showToast('Item deleted');
    } catch { showToast('Failed to delete', 'error'); }
  };

  // ── reorder (move up / move down) ───────────────────────────────────────

  const moveItem = async (idx: number, dir: -1 | 1) => {
    if (!data) return;
    const items = [...data.items];
    const swap = idx + dir;
    if (swap < 0 || swap >= items.length) return;
    [items[idx], items[swap]] = [items[swap], items[idx]];
    const reordered = items.map((it, i) => ({ ...it, displayOrder: i }));
    setData(prev => prev ? { ...prev, items: reordered } : prev);
    try {
      await faqAdminService.reorderItems(reordered.map(it => ({ id: it.id, displayOrder: it.displayOrder })));
    } catch { showToast('Failed to reorder', 'error'); load(); }
  };

  // ── guards ──────────────────────────────────────────────────────────────

  if (!canView) {
    return (
      <div style={{ padding: 32, textAlign: 'center', color: '#64748b' }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>🔒</div>
        <p>You need <strong>VIEW_FAQ</strong> or <strong>MANAGE_FAQ</strong> permission to access this section.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 240 }}>
        <svg style={{ width: 32, height: 32, color: '#94a3b8', animation: 'spin 1s linear infinite' }} fill="none" viewBox="0 0 24 24">
          <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div style={{ maxWidth: 800, margin: '0 auto', padding: '0 4px 40px' }}>

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: 20, right: 20, zIndex: 9999,
          background: toast.type === 'error' ? '#fef2f2' : '#f0fdf4',
          border: `1px solid ${toast.type === 'error' ? '#fecaca' : '#bbf7d0'}`,
          borderRadius: 10, padding: '10px 18px', fontSize: 13, fontWeight: 600,
          color: toast.type === 'error' ? '#dc2626' : '#16a34a',
          boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
        }}>
          {toast.type === 'error' ? '✗' : '✓'} {toast.msg}
        </div>
      )}

      {/* ── 1. Publish status ────────────────────────────────────────────── */}
      <Card style={{ background: data.published ? '#f0fdf4' : '#fffbeb', border: `1.5px solid ${data.published ? '#86efac' : '#fde68a'}` }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#1e293b' }}>
              {data.published ? '🟢 FAQ page is live' : '🟡 FAQ page is unpublished'}
            </div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 3 }}>
              {data.published
                ? 'Visitors can access the FAQ at /faq'
                : 'The /faq page redirects to home until published'}
            </div>
            {data.updatedBy && (
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                Last edited by {data.updatedBy}{data.updatedAt ? ` · ${new Date(data.updatedAt).toLocaleString()}` : ''}
              </div>
            )}
          </div>
          {canManage && (
            <button
              onClick={data.published ? handleUnpublish : handlePublish}
              disabled={publishing}
              style={{
                background: data.published ? '#fef2f2' : '#2563eb',
                color: data.published ? '#dc2626' : '#fff',
                border: data.published ? '1px solid #fecaca' : 'none',
                borderRadius: 8, padding: '8px 20px', fontSize: 13, fontWeight: 700,
                cursor: publishing ? 'not-allowed' : 'pointer', opacity: publishing ? 0.6 : 1,
              }}
            >
              {publishing ? '…' : data.published ? 'Unpublish' : 'Publish FAQ'}
            </button>
          )}
        </div>
      </Card>

      {/* ── 2. Mask all ──────────────────────────────────────────────────── */}
      <Card>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px', borderRadius: 10,
          background: data.maskAll ? '#fef2f2' : '#f8fafc',
          border: `1.5px solid ${data.maskAll ? '#fecaca' : '#e2e8f0'}` }}>
          <span style={{ fontSize: 22 }}>{data.maskAll ? '🙈' : '👁️'}</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: 13, color: '#1e293b' }}>
              {data.maskAll ? 'All FAQ content is masked' : 'FAQ content is visible'}
            </div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 1 }}>
              When masked, questions and answers are replaced with placeholder text — real content is never sent to the browser.
            </div>
          </div>
          <Toggle checked={!!data.maskAll} onChange={handleToggleMask} disabled={!canManage} />
        </div>
      </Card>

      {/* ── 3. Page settings ─────────────────────────────────────────────── */}
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <span style={{ fontWeight: 700, fontSize: 14, color: '#1e293b' }}>📄 Page Settings</span>
          {canManage && !configEditing && (
            <button onClick={() => setConfigEditing(true)}
              style={{ background: '#eff6ff', color: '#2563eb', border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
              ✏️ Edit
            </button>
          )}
        </div>
        {!configEditing ? (
          <div style={{ background: '#f8fafc', borderRadius: 10, padding: '12px 14px' }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#1e293b', marginBottom: 4 }}>{data.pageTitle || '(no title)'}</div>
            <div style={{ fontSize: 12, color: '#64748b' }}>{data.pageSubtitle || '(no subtitle)'}</div>
          </div>
        ) : (
          <>
            <Input label="Page Title" value={configForm.pageTitle} onChange={v => setConfigForm(f => ({ ...f, pageTitle: v }))} placeholder="Frequently Asked Questions" />
            <Input label="Page Subtitle" value={configForm.pageSubtitle} onChange={v => setConfigForm(f => ({ ...f, pageSubtitle: v }))} placeholder="Everything you need to know about Zogzila." />
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={handleConfigSave} disabled={saving}
                style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 24px', fontSize: 13, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1 }}>
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button onClick={() => { setConfigEditing(false); setConfigForm({ pageTitle: data.pageTitle, pageSubtitle: data.pageSubtitle }); }}
                style={{ background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 8, padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </>
        )}
      </Card>

      {/* ── 4. FAQ Items ─────────────────────────────────────────────────── */}
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <span style={{ fontWeight: 700, fontSize: 14, color: '#1e293b' }}>❓ FAQ Items ({data.items.length})</span>
          {canManage && (
            <button onClick={() => { setAddingItem(true); setNewItemForm({ question: '', answer: '' }); }}
              style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, padding: '7px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
              + Add Item
            </button>
          )}
        </div>

        {/* Add new item form */}
        {addingItem && (
          <div style={{ background: '#eff6ff', border: '1.5px solid #bfdbfe', borderRadius: 10, padding: '16px 16px 12px', marginBottom: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: '#1e40af', marginBottom: 10 }}>New FAQ Item</div>
            <Input label="Question" value={newItemForm.question} onChange={v => setNewItemForm(f => ({ ...f, question: v }))} placeholder="What is Zogzila?" />
            <Textarea label="Answer" value={newItemForm.answer} onChange={v => setNewItemForm(f => ({ ...f, answer: v }))} placeholder="Zogzila is…" rows={3} />
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={handleAddItem} disabled={saving}
                style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 20px', fontSize: 13, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1 }}>
                {saving ? 'Saving…' : 'Add'}
              </button>
              <button onClick={() => setAddingItem(false)}
                style={{ background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {data.items.length === 0 && !addingItem && (
          <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: 13, padding: '24px 0' }}>
            No FAQ items yet. Click "+ Add Item" to create the first one.
          </div>
        )}

        {data.items.map((item, idx) => (
          <div key={item.id} style={{
            border: `1.5px solid ${!item.visible ? '#fde68a' : '#e2e8f0'}`,
            borderRadius: 10, marginBottom: 10, overflow: 'hidden',
            background: !item.visible ? '#fffbeb' : '#fff',
          }}>
            {editingItemId === item.id ? (
              <div style={{ padding: '14px 16px' }}>
                <Input label="Question" value={editItemForm.question} onChange={v => setEditItemForm(f => ({ ...f, question: v }))} />
                <Textarea label="Answer" value={editItemForm.answer} onChange={v => setEditItemForm(f => ({ ...f, answer: v }))} rows={3} />
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => handleEditSave(item.id)} disabled={saving}
                    style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, padding: '7px 20px', fontSize: 13, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1 }}>
                    {saving ? 'Saving…' : 'Save'}
                  </button>
                  <button onClick={() => setEditingItemId(null)}
                    style={{ background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 8, padding: '7px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ padding: '12px 16px' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: '#1e293b', marginBottom: 4 }}>
                      {!item.visible && <span style={{ fontSize: 10, background: '#fef3c7', color: '#92400e', padding: '1px 6px', borderRadius: 8, marginRight: 6, fontWeight: 700 }}>HIDDEN</span>}
                      {item.question}
                    </div>
                    <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{item.answer}</div>
                  </div>
                  {canManage && (
                    <div style={{ display: 'flex', gap: 4, flexShrink: 0, marginTop: 2 }}>
                      {/* Reorder */}
                      <button onClick={() => moveItem(idx, -1)} disabled={idx === 0}
                        title="Move up"
                        style={{ background: '#f1f5f9', border: 'none', borderRadius: 6, padding: '4px 8px', fontSize: 12, cursor: idx === 0 ? 'not-allowed' : 'pointer', opacity: idx === 0 ? 0.4 : 1 }}>↑</button>
                      <button onClick={() => moveItem(idx, 1)} disabled={idx === data.items.length - 1}
                        title="Move down"
                        style={{ background: '#f1f5f9', border: 'none', borderRadius: 6, padding: '4px 8px', fontSize: 12, cursor: idx === data.items.length - 1 ? 'not-allowed' : 'pointer', opacity: idx === data.items.length - 1 ? 0.4 : 1 }}>↓</button>
                      {/* Visibility */}
                      <button onClick={() => handleToggleItemVisible(item)}
                        title={item.visible ? 'Hide item' : 'Show item'}
                        style={{ background: item.visible ? '#f0fdf4' : '#fef9c3', border: 'none', borderRadius: 6, padding: '4px 8px', fontSize: 12, cursor: 'pointer' }}>
                        {item.visible ? '👁️' : '🚫'}
                      </button>
                      {/* Edit */}
                      <button onClick={() => startEdit(item)}
                        style={{ background: '#eff6ff', color: '#2563eb', border: 'none', borderRadius: 6, padding: '4px 8px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>✏️</button>
                      {/* Delete */}
                      <button onClick={() => handleDelete(item.id)}
                        style={{ background: '#fef2f2', color: '#dc2626', border: 'none', borderRadius: 6, padding: '4px 8px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>🗑️</button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </Card>

      {/* ── Preview link ─────────────────────────────────────────────────── */}
      <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 12, padding: '14px 20px', fontSize: 13, color: '#92400e' }}>
        <strong>Preview</strong> — visit{' '}
        <a href="/faq" target="_blank" rel="noreferrer" style={{ color: '#b45309', fontWeight: 700 }}>/faq</a>{' '}
        to see the live public FAQ page.{' '}
        {!data.published && <span>The page currently redirects to home — <strong>publish</strong> it to make it accessible.</span>}
      </div>
    </div>
  );
};

export default AdminFaqManager;
